import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canSee } from "@/lib/sections";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Enquiries from "@/pages/Enquiries";
import Customers from "@/pages/Customers";
import Venues from "@/pages/Venues";
import Fleet from "@/pages/Fleet";
import Providers from "@/pages/Providers";
import Bookings from "@/pages/Bookings";
import BookingDetail from "@/pages/BookingDetail";
import PaymentResult from "@/pages/PaymentResult";
import Menus from "@/pages/Menus";
import Staffing from "@/pages/Staffing";
import Equipment from "@/pages/Equipment";
import Tasks from "@/pages/Tasks";
import Reports from "@/pages/Reports";
import EventSheet from "@/pages/EventSheet";
import InvoiceView from "@/pages/InvoiceView";
import ClientPortal from "@/pages/ClientPortal";
import Landing from "@/pages/Landing";
import HubPage from "@/pages/HubPage";
import Companies from "@/pages/Companies";
import BusinessCompanies from "@/pages/BusinessCompanies";
import HubSettings from "@/pages/HubSettings";
import Orders from "@/pages/Orders";
import Toolkit from "@/pages/Toolkit";
import BuffetOrder from "@/pages/BuffetOrder";
import AdminPlans from "@/pages/AdminPlans";
import AdminPlatform from "@/pages/AdminPlatform";
import AdminEmails from "@/pages/AdminEmails";
import AdminHealth from "@/pages/AdminHealth";
import EmbedForm from "@/pages/EmbedForm";
import ReviewPage from "@/pages/ReviewPage";
import PrepSheet from "@/pages/PrepSheet";
import RoutesBoard from "@/pages/RoutesBoard";
import ResetPassword from "@/pages/ResetPassword";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="auth-loading">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function CompaniesGate() {
  const { user } = useAuth();
  if (!canSee(user, "/companies")) return <Navigate to="/dashboard" replace />;
  return user?.company_id ? <BusinessCompanies /> : <Companies />;
}

function AdminGate({ children }) {
  const { user } = useAuth();
  return user?.platform_role === "super_admin" ? children : <Navigate to="/dashboard" replace />;
}

function TenantGate() {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.platform_role === "super_admin" && !user?.impersonating) return <Navigate to="/admin/vendors" replace />;
  if (!canSee(user, location.pathname)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/hub/:slug" element={<HubPage />} />
            <Route path="/embed/hub/:slug" element={<EmbedForm />} />
            <Route path="/review/:token" element={<ReviewPage />} />
            <Route path="/hub/:slug/order" element={<BuffetOrder />} />
            <Route path="/hub/:slug/onsite" element={<BuffetOrder onsite />} />
            <Route path="/portal/:token" element={<ClientPortal />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route element={<TenantGate />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="hub-settings" element={<HubSettings />} />
                <Route path="enquiries" element={<Enquiries />} />
                <Route path="customers" element={<Customers />} />
                <Route path="venues" element={<Venues />} />
                <Route path="fleet" element={<Fleet />} />
                <Route path="providers" element={<Providers />} />
                <Route path="menus" element={<Menus />} />
                <Route path="staffing" element={<Staffing />} />
                <Route path="equipment" element={<Equipment />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="reports" element={<Reports />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="orders" element={<Orders />} />
                <Route path="prep-sheet" element={<PrepSheet />} />
                <Route path="routes" element={<RoutesBoard />} />
                <Route path="toolkit" element={<Toolkit />} />
                <Route path="bookings/:id" element={<BookingDetail />} />
                <Route path="bookings/:id/sheet" element={<EventSheet />} />
                <Route path="invoices/:invId" element={<InvoiceView />} />
              </Route>
              <Route path="companies" element={<CompaniesGate />} />
              <Route path="admin/vendors" element={<AdminGate><Companies /></AdminGate>} />
              <Route path="admin/plans" element={<AdminGate><AdminPlans /></AdminGate>} />
              <Route path="admin/platform" element={<AdminGate><AdminPlatform /></AdminGate>} />
              <Route path="admin/emails" element={<AdminGate><AdminEmails /></AdminGate>} />
              <Route path="admin/health" element={<AdminGate><AdminHealth /></AdminGate>} />
              <Route path="payment/success" element={<PaymentResult kind="success" />} />
              <Route path="payment/cancel" element={<PaymentResult kind="cancel" />} />
              <Route path="payment/success" element={<PaymentResult kind="success" />} />
              <Route path="payment/cancel" element={<PaymentResult kind="cancel" />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
